// BUILD-042 October 10 14:05/14:08 Phoenix addenda — "Move to History" dialog for a completed
// employee document record. Authority: docs/superpowers/decisions/
//   2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md
//
// HR selects a predefined, currently-ACTIVE reason (never a free-text/"Other" option — the
// list comes from the server's own admin-managed registry) and supplies exactly the required
// detail for that reason. The four protected reasons each have their own typed fields; a new
// admin-created reason only ever asks for the single bounded generic supporting-detail text
// field. All relationship/typed validation happens server-side (index.ts's
// handleMoveDocumentAssignmentToHistory) — this dialog only collects what that validation
// requires and surfaces the server's own rejection message if something is missing/invalid,
// never silently guessing or auto-selecting a candidate.
import { useEffect, useState } from 'react';
import {
  listHrDocumentHistoryReasons, moveHrDocumentAssignmentToHistory, listHrEligibleReplacementCandidates,
  type HrDocumentHistoryReason, type HrDocumentAssignment,
} from '../../../api/client';
import { formatLocalDate } from '../hrFormat';

export function MoveToHistoryModal({
  assignment, employeeId, isEquipmentTemplate, onClose, onMoved,
}: {
  assignment: HrDocumentAssignment;
  employeeId: string;
  /** Whether the assignment's OWN template was explicitly classified record_kind='equipment' —
   *  passed in by the caller (which already has the template list), so this dialog never has
   *  to guess from the document's name. Used only to decide whether to even OFFER the
   *  "Equipment returned" reason as a sensible choice; the server performs the real check
   *  regardless. */
  isEquipmentTemplate: boolean;
  onClose: () => void;
  onMoved: (message: string) => void;
}) {
  const [reasons, setReasons] = useState<HrDocumentHistoryReason[]>([]);
  const [reasonId, setReasonId] = useState('');
  const [detailText, setDetailText] = useState('');
  const [candidates, setCandidates] = useState<HrDocumentAssignment[] | null>(null);
  const [linkedAssignmentId, setLinkedAssignmentId] = useState('');
  const [equipmentReturnDate, setEquipmentReturnDate] = useState('');
  const [equipmentReceivedBy, setEquipmentReceivedBy] = useState('');
  const [equipmentIdentified, setEquipmentIdentified] = useState('');
  const [equipmentWholeIssuanceConfirmed, setEquipmentWholeIssuanceConfirmed] = useState(false);
  const [filedInErrorConfirmed, setFiledInErrorConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listHrDocumentHistoryReasons().then((d) => setReasons(d.reasons)).catch(() => setReasons([]));
  }, []);

  const selectedReason = reasons.find((r) => r.reasonId === reasonId) ?? null;
  const needsReplacementLink = selectedReason?.code === 'replaced_by_newer' || selectedReason?.code === 'duplicate_record';
  const needsEquipmentFields = selectedReason?.code === 'equipment_returned';
  const needsFiledInErrorConfirm = selectedReason?.code === 'filed_in_error';
  // October 10 21:10 addendum: supporting detail is UNCONDITIONALLY optional for every
  // generic (non-protected) reason — never gated on this reason's own stored
  // requiresSupportingDetail flag. That flag predates the Oct 10 18:22 pass for any reason
  // created earlier (e.g. "Because it was old") and was never retroactively updated; the
  // approved policy applies to every generic reason alike, old or new, so the field itself is
  // always shown but never required. isGenericReason still gates whether the field renders at
  // all (a protected reason has its own typed fields instead).
  const isGenericReason = selectedReason ? !selectedReason.isProtected : false;

  useEffect(() => {
    if (needsReplacementLink && candidates === null) {
      listHrEligibleReplacementCandidates(employeeId, assignment.assignmentId)
        .then((d) => setCandidates(d.candidates))
        .catch(() => setCandidates([]));
    }
  }, [needsReplacementLink, candidates, employeeId, assignment.assignmentId]);

  function canSubmit(): boolean {
    if (!selectedReason) return false;
    if (needsReplacementLink && !linkedAssignmentId) return false;
    if (needsEquipmentFields && (!equipmentReturnDate || !equipmentReceivedBy.trim() || !equipmentIdentified.trim() || !equipmentWholeIssuanceConfirmed)) return false;
    if (needsFiledInErrorConfirm && !filedInErrorConfirmed) return false;
    // Supporting detail is never required to submit — see isGenericReason comment above.
    return true;
  }

  async function handleSubmit() {
    if (!canSubmit() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await moveHrDocumentAssignmentToHistory(assignment.assignmentId, {
        reasonId,
        detailText: detailText.trim() || null,
        linkedAssignmentId: needsReplacementLink ? linkedAssignmentId : null,
        equipmentReturnDate: needsEquipmentFields ? equipmentReturnDate : null,
        equipmentReceivedBy: needsEquipmentFields ? equipmentReceivedBy.trim() : null,
        equipmentIdentified: needsEquipmentFields ? equipmentIdentified.trim() : null,
        equipmentWholeIssuanceConfirmed: needsEquipmentFields ? equipmentWholeIssuanceConfirmed : null,
        filedInErrorConfirmed: needsFiledInErrorConfirm ? filedInErrorConfirmed : null,
      });
      onMoved(`Archived — "${selectedReason!.label}". The completed copy remains available to view/download under Archived records.`);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to move this record to History.');
    } finally {
      setSubmitting(false);
    }
  }

  // October 10 18:22 addendum: "offer only applicable reasons" — equipment_returned is never
  // offered at all unless this document's own template was explicitly classified
  // record_kind='equipment' (never guessed from the document's name). The server independently
  // re-validates this at submit time; hiding it here just means HR never sees a reason that
  // would be refused, rather than relying on the amber warning as the only signal.
  const activeReasons = reasons.filter((r) => !r.retiredAt && (r.code !== 'equipment_returned' || isEquipmentTemplate));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="move-to-history-title" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-gray-100">
          <h2 id="move-to-history-title" className="text-sm font-semibold text-gray-700">Archive "{assignment.templateName}"</h2>
        </div>
        <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* October 10 18:22 addendum: short, explicit confirmation that the completed copy
              stays viewable/downloadable — the specific reassurance that was missing before. */}
          <p className="text-xs text-gray-500">
            This moves the record to Archived records. The completed copy stays available to view and download there. It never automatically returns equipment, supersedes another record, changes review completion, satisfies a checklist, or invalidates a signature.
          </p>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Reason</label>
            <select
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full"
              value={reasonId}
              onChange={(e) => { setReasonId(e.target.value); setCandidates(null); setLinkedAssignmentId(''); }}
            >
              <option value="">Choose a reason…</option>
              {activeReasons.map((r) => (
                <option key={r.reasonId} value={r.reasonId}>{r.label}</option>
              ))}
            </select>
            {selectedReason && <div className="text-[11px] text-gray-400 mt-1">{selectedReason.explanation}</div>}
          </div>

          {needsReplacementLink && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                {selectedReason?.code === 'replaced_by_newer' ? 'Select the actual replacement record kept for this employee' : 'Select the duplicate record being kept for this employee'}
              </label>
              {selectedReason?.code === 'replaced_by_newer' && (
                <p className="text-[11px] text-gray-400 mb-1.5">
                  "Replaced by newer completed document" requires an actual reviewed-complete replacement that already exists for the same employee and the same form — pick it below. The list shows only this employee's completed, non-archived records; a blank template, a pending assignment, or another employee's record is never eligible. The replacement is kept; this record moves to History.
                </p>
              )}
              {candidates === null ? (
                <div className="text-xs text-gray-400">Loading eligible records…</div>
              ) : candidates.length === 0 ? (
                <div className="text-xs text-amber-700">No other completed, non-archived record exists for this employee to select. A blank template or pending assignment is not a valid choice.</div>
              ) : (
                <select className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" value={linkedAssignmentId} onChange={(e) => setLinkedAssignmentId(e.target.value)}>
                  <option value="">Choose a record…</option>
                  {candidates.map((c) => (
                    <option key={c.assignmentId} value={c.assignmentId}>{c.templateName} (v{c.versionNumber}, assigned {formatLocalDate(c.assignedAt)})</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {needsEquipmentFields && (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Return date</label>
                <input type="date" className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" value={equipmentReturnDate} onChange={(e) => setEquipmentReturnDate(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Received by</label>
                <input className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" placeholder="HR person who received it" value={equipmentReceivedBy} onChange={(e) => setEquipmentReceivedBy(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Equipment identified</label>
                <input className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" placeholder="e.g. Laptop, asset tag FT-0001" value={equipmentIdentified} onChange={(e) => setEquipmentIdentified(e.target.value)} />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-600">
                <input type="checkbox" className="mt-0.5" checked={equipmentWholeIssuanceConfirmed} onChange={(e) => setEquipmentWholeIssuanceConfirmed(e.target.checked)} />
                I confirm the WHOLE issuance was returned. A partial return cannot close this record.
              </label>
            </div>
          )}

          {needsFiledInErrorConfirm && (
            <label className="flex items-start gap-2 text-xs text-gray-600">
              <input type="checkbox" className="mt-0.5" checked={filedInErrorConfirmed} onChange={(e) => setFiledInErrorConfirmed(e.target.checked)} />
              I confirm this record was filed against this employee in error.
            </label>
          )}

          {/* October 10 21:10 addendum: supporting detail is ALWAYS optional for a generic
              (non-protected) reason — old or new — never conditioned on this reason's own
              stored requiresSupportingDetail flag (see isGenericReason comment above for why
              that flag can no longer be trusted as a per-reason requirement). */}
          {isGenericReason && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">Supporting detail (optional)</label>
              <textarea className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" rows={3} value={detailText} onChange={(e) => setDetailText(e.target.value)} />
            </div>
          )}

          {error && <div className="text-xs text-red-700">{error}</div>}
        </div>
        <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-gray-600 px-4 py-1.5 rounded hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit() || submitting} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? 'Archiving…' : 'Archive record'}
          </button>
        </div>
      </div>
    </div>
  );
}
